import { HttpClient } from '@angular/common/http';
import { Injectable, Injector } from '@angular/core';
import { Router } from '@angular/router';
import { BehaviorSubject, Observable } from 'rxjs';
import { AwsAuthService } from 'src/app/services/aws-auth.service';
import { DateService } from 'src/app/services/date.service';
import { SharedDataService } from 'src/app/services/shared-data.service';
import { environment } from 'src/environments/environment';


export interface UserDataLogin {
  id: number,
  username: string,
  name: string,
  firstname: string,
  lastname: string,
  enabled: boolean,
  role: string,
  department: string,
  userType: string,
  hasAgreedToTerms: boolean,
  customerId: number,
  communityId: number,
  logoUrl: string,
  residentId: number,
  avatarUrl?: string,

  // Community configuration - populated for COMMUNITY_USERS
  communityConfig?: CommunityConfig
}

export interface DepartmentLogin {
  id: number,
  name: string,
  section: string
}

export interface PermissionLogin {
  id: number,
  permissionDescription: string,
  permissionCode: string
}

export interface RoleLogin {
  id: number,
  roleDescription: string,
  roleName: string,
  department: DepartmentLogin
  permissions: PermissionLogin[]
}

export interface CommunityConfig {
  communityId: number,
  communityName: string,
  configured: boolean,
  timeZone: string,
  communityLogoUrl: string,
  hasReportingDates: boolean,
  censusStartDate?: string,
  healthStartDate?: string,
  revenueStartDate?: string
}

export interface UserDataResponse {
  refreshToken: string,
  userData: UserDataLogin,
  role: RoleLogin,

  // NEW: Community configuration - populated for COMMUNITY_USERS
  communityConfig?: CommunityConfig,

  // usually blank parameters:
  accessToken: string,
  expiresIn: string,
  actualDate: string,
  expirationDate: string,
  username: string,
  message: string,
  userStatus: string,
  departmentsList: any
}

@Injectable({
  providedIn: 'root'
})
export class LoginService {

  private currentUser = new BehaviorSubject<UserDataResponse>(null);
  currentUser$ = this.currentUser.asObservable();

  changeCurrentUser(message: UserDataResponse) {
    this.currentUser.next(message);
  }

  getCurrentUser(): UserDataResponse {
    return this.currentUser.getValue();
  }

  private customerName = new BehaviorSubject(null);
  customerName$ = this.customerName.asObservable();

  changeCustomerName(message: string) {
    this.customerName.next(message);
  }

  private residentName = new BehaviorSubject(null);
  residentName$ = this.residentName.asObservable();

  changeResidentName(message: string) {
    this.residentName.next(message);
  }

  private residentId = new BehaviorSubject('');
  residentId$ = this.residentId.asObservable();

  changeResidentId(data: any) {
    this.residentId.next(data);
  }

  private communityId = new BehaviorSubject(null);
  communityId$ = this.communityId.asObservable();

  changeCommunityId(communityId: string) {
    this.communityId.next(communityId);
  }

  private communityName = new BehaviorSubject(null);
  communityName$ = this.communityName.asObservable();

  changeCommunityName(communityName: string) {
    this.communityName.next(communityName);
  }

  private operatorId = new BehaviorSubject('');
  operatorId$ = this.operatorId.asObservable();

  changeOperatorId(operatorId: any) {
    this.operatorId.next(operatorId);
  }

  /**
   * Profile avatar img url
   */
  private profileImgUrl = new BehaviorSubject('');
  profileImgUrl$ = this.profileImgUrl.asObservable();

  changeProfileImgUrl(profileImgUrl: string) {
    this.profileImgUrl.next(profileImgUrl);
  }

  /**
    * Logo img url
    */
  private logoUrl = new BehaviorSubject('');
  logoUrl$ = this.logoUrl.asObservable();

  changeLogoUrl(logoUrl: string) {
    this.logoUrl.next(logoUrl);
  }

  /**
    * Role Name
    */
   private role = new BehaviorSubject('');
   role$ = this.role.asObservable();

   changeRole(role: string) {
     this.role.next(role);
   }

  constructor(private awsAuthService: AwsAuthService,
    private router: Router,
    private http: HttpClient,
    private injector: Injector
  ) { }

  loadUserDetails(skipNavigation: boolean = false) {
    this.awsAuthService.getCurrentUser((err, user: any) => {
      if (!user || !user.signedIn) {
        return;
      }
      this.awsAuthService.updateCredentials();
      this.getUserData().subscribe(res => this.setLoggedInUser(res.data, skipNavigation));
    });
  }

  setLoggedInUser(user: UserDataResponse, skipNavigation: boolean = false) {
    console.log('setLoggedInUser called with user:', user);
    console.log('user.userData:', user.userData);
    console.log('user.userData.communityConfig:', user.userData?.communityConfig);
    console.log('Does userData have communityConfig?', user.userData && 'communityConfig' in user.userData);

    // the logged in user
    this.changeCurrentUser(user);

    // Always set critical IDs needed for API calls
    this.changeCommunityId(user.userData.communityId + "");
    this.changeOperatorId(user.userData.customerId);
    this.changeLogoUrl(user.userData.logoUrl);
    this.changeRole(user.role.roleDescription);

    // NEW: For COMMUNITY_USERS, use enhanced community config to avoid extra API calls
    if (user.userData?.communityConfig && user.userData.userType === 'COMMUNITY_USERS') {
      // Use lazy loading to avoid circular dependencies
      const dateService = this.injector.get(DateService);
      const sharedDataService = this.injector.get(SharedDataService);

      // Set timezone immediately from communityConfig
      if (dateService && user.userData.communityConfig.timeZone) {
        dateService.changeTimeZoneValue(user.userData.communityConfig.timeZone);
      }

      // Set community configured status
      if (sharedDataService) {
        sharedDataService.changeIsCommunityConfiguredValue(user.userData.communityConfig.configured ? 'true' : 'false');

        // Set community logo if available
        if (user.userData.communityConfig.communityLogoUrl) {
          sharedDataService.changeCommunityLogoUrl(user.userData.communityConfig.communityLogoUrl);
        }
      }

      // Store reporting dates if available
      if (user.userData.communityConfig.hasReportingDates) {
        if (user.userData.communityConfig.censusStartDate) {
          localStorage.setItem('censusStartDate', user.userData.communityConfig.censusStartDate);
        }
        if (user.userData.communityConfig.healthStartDate) {
          localStorage.setItem('healthStartDate', user.userData.communityConfig.healthStartDate);
        }
        if (user.userData.communityConfig.revenueStartDate) {
          localStorage.setItem('revenueStartDate', user.userData.communityConfig.revenueStartDate);
        }
        localStorage.setItem('reportingDatesTimestamp', new Date().toISOString());
      }
    }

    // For resident-specific data, check if we should preserve existing selection
    if (skipNavigation) {
      // On page refresh with deep link, only update resident if not already set
      const currentResidentId = this.residentId.getValue();

      // If no resident is currently selected, use the logged-in user's defaults
      if (!currentResidentId || currentResidentId === '') {
        this.changeResidentId(user.userData.residentId);
        this.changeProfileImgUrl(user.userData.avatarUrl);
      }
      // Otherwise preserve the currently selected resident
    } else {
      // Fresh login or navigation - use logged-in user's defaults
      this.changeResidentId(user.userData.residentId);
      this.changeProfileImgUrl(user.userData.avatarUrl);
    }

    localStorage.setItem('username', user.userData.username);

    // Skip navigation if requested (e.g., when loading user on app init with existing route)
    if (skipNavigation) {
      return;
    }

    // Don't navigate to dashboard if we're on a print route
    const currentUrl = this.router.url;
    const isPrintRoute = currentUrl.includes('/print');

    if (!isPrintRoute) {
      // Only process stored return URL if deep links are enabled for non-authenticated users
      if (environment.enableDeepLinksPreLogin) {
        // Check if there's a stored return URL from before login
        const returnUrl = localStorage.getItem('returnUrl');

        if (returnUrl) {
          localStorage.removeItem('returnUrl'); // Clean up after use
          this.router.navigate([returnUrl]);
          return;
        }
      }

      // Default navigation to dashboard
      //if (this.checkUserAgreedToTerms(user)) {
      // this.router.navigate(['operator/terms-and-conditions']);
      this.router.navigate(['dashboard']);
    }
  }

  getUserData(): Observable<any> {
    return this.http.get(environment.environmentDetails + 'user-service/' + 'auth/getUserData');
  }
}
